import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { Mail, MessageSquare, ChevronRight, Eye, EyeOff, User, Lock } from 'lucide-react';
import { motion } from 'framer-motion';
import { useAuth } from '../../../../../../../context/auth/AuthContext';
import CustomInput from '../../../../../../../components/forms/CustomInput';
import RoleLockedModal from './RoleLockedModal';

const LoginForm = ({ onSwitchToRegister }) => {
  const navigate = useNavigate();
  const {
    login,
    sendEmailOtp,
    sendSmsOtp,
    verifyEmailOtp,
    verifySmsOtp,
    resetPassword: authResetPassword
  } = useAuth();

  // Standard Auth States
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // OTP Logic States
  const [isOtpSelectionView, setIsOtpSelectionView] = useState(false);
  const [isEmailOtpView, setIsEmailOtpView] = useState(false);
  const [isSmsOtpView, setIsSmsOtpView] = useState(false);
  const [email, setEmail] = useState('');
  const [smsPhone, setSmsPhone] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [smsOtpSent, setSmsOtpSent] = useState(false);
  const [emailOtp, setEmailOtp] = useState(['', '', '', '', '', '']);
  const [smsOtp, setSmsOtp] = useState(['', '', '', '', '', '']);
  const [otpIdentifier, setOtpIdentifier] = useState('');

  // Forgot Password States
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [forgotPasswordMethod, setForgotPasswordMethod] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPasswordForm, setShowNewPasswordForm] = useState(false);

  // Role Locked Modal State
  const [roleLockedInfo, setRoleLockedInfo] = useState(null);

  const handleLoginSuccess = (data) => {
    const tokenPayload = JSON.parse(atob(data.token.split('.')[1]));
    localStorage.setItem('token_agents', data.token);
    localStorage.setItem('agent_id', data.agent_id || tokenPayload.agent_id || tokenPayload.business_id);
    localStorage.setItem('user_id', data.user._id);
    localStorage.setItem('sub_user_role', data.sub_user_role || tokenPayload.sub_user_role || 'admin');
    localStorage.setItem('agent_active_tab', 'home');

    if (data.needsProfiling) {
      localStorage.setItem('needs_profiling', 'true');
      toast.success("Welcome! Let's complete your profile.");
      setTimeout(() => {
        navigate('/dashboard', {
          state: {
            showProfiling: true,
            agentId: tokenPayload.business_id,
            companyName: data.user.name
          }
        });
      }, 500);
    } else {
      toast.success('Accessing Portal...');
      setTimeout(() => {
        window.location.href = '/dashboard';
      }, 500);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!phone || !password) {
      toast.error('Fields required');
      return;
    }
    setIsLoading(true);
    try {
      const response = await login({ identifier: phone, password, role: 'Agent' });
      if (response.message === 'Login successful') {
        handleLoginSuccess(response);
      }
    } catch (error) {
      if (error.response?.data?.isRoleLocked) {
        setRoleLockedInfo(error.response.data);
      } else if (
        error.response?.data?.isWaiting ||
        (error.response?.status === 403 &&
          error.response?.data?.message?.toLowerCase().includes('approved'))
      ) {
        const ident = phone;
        if (ident) localStorage.setItem('pending_agent_identifier', ident);
        navigate('/waiting', { state: { isWaiting: true, identifier: ident } });
      } else {
        toast.error(error.response?.data?.message || 'Invalid Credentials');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendOtp = async (e) => {
    e?.preventDefault();
    if (!otpIdentifier) {
      toast.error('Please enter email or phone number');
      return;
    }

    setIsLoading(true);
    try {
      const isEmail = otpIdentifier.includes('@');
      if (isEmail) {
        setEmail(otpIdentifier);
        const response = await sendEmailOtp({ email: otpIdentifier, role: 'Agent' });
        if (response.message === 'OTP sent to your email') {
          setOtpSent(true);
          setIsEmailOtpView(true);
          setIsOtpSelectionView(false);
          toast.success('OTP sent to your email!');
        }
      } else {
        const cleanPhone = otpIdentifier.replace(/\D/g, '');
        if (cleanPhone.length !== 10) {
          toast.error('Please enter a valid 10-digit phone number');
          setIsLoading(false);
          return;
        }
        setSmsPhone(cleanPhone);
        const response = await sendSmsOtp({ phone_number: cleanPhone, role: 'Agent' });
        if (response.message === 'OTP sent successfully') {
          setSmsOtpSent(true);
          setIsSmsOtpView(true);
          setIsOtpSelectionView(false);
          toast.success('OTP sent to your phone!');
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to send OTP');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyEmailOtp = async (e) => {
    e?.preventDefault();
    const otpString = emailOtp.join('');
    if (otpString.length !== 6) {
      toast.error('Enter 6-digit OTP');
      return;
    }
    setIsLoading(true);
    try {
      const response = await verifyEmailOtp({ email, otp: otpString });
      if (isForgotPassword) {
        setShowNewPasswordForm(true);
        toast.success('OTP verified!');
      } else if (response.message === 'Login Successful') {
        handleLoginSuccess(response);
      }
    } catch (error) {
      if (error.response?.data?.isRoleLocked) {
        setRoleLockedInfo(error.response.data);
      } else if (
        error.response?.data?.isWaiting ||
        (error.response?.status === 403 &&
          error.response?.data?.message?.toLowerCase().includes('approved'))
      ) {
        const ident = email;
        if (ident) localStorage.setItem('pending_agent_identifier', ident);
        navigate('/waiting', { state: { isWaiting: true, identifier: ident } });
      } else {
        toast.error(error.response?.data?.message || 'Verification failed');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifySmsOtp = async (e) => {
    e?.preventDefault();
    const otpString = smsOtp.join('');
    if (otpString.length !== 6) {
      toast.error('Enter 6-digit OTP');
      return;
    }
    setIsLoading(true);
    try {
      const response = await verifySmsOtp({ phone_number: smsPhone, otp: otpString });
      if (isForgotPassword) {
        setShowNewPasswordForm(true);
        toast.success('OTP verified!');
      } else if (response.message === 'Login successful') {
        handleLoginSuccess(response);
      }
    } catch (error) {
      if (error.response?.data?.isRoleLocked) {
        setRoleLockedInfo(error.response.data);
      } else if (
        error.response?.data?.isWaiting ||
        (error.response?.status === 403 &&
          error.response?.data?.message?.toLowerCase().includes('approved'))
      ) {
        const ident = smsPhone;
        if (ident) localStorage.setItem('pending_agent_identifier', ident);
        navigate('/waiting', { state: { isWaiting: true, identifier: ident } });
      } else {
        toast.error(error.response?.data?.message || 'Verification failed');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const resetPassword = async (e) => {
    e?.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    if (!newPassword) {
      toast.error('Password cannot be empty');
      return;
    }
    setIsLoading(true);
    try {
      const payload = {
        newPassword,
        ...(forgotPasswordMethod === 'email' ? { email } : { phone_number: smsPhone })
      };
      await authResetPassword(payload);
      toast.success('Password reset successful!');
      resetToLogin();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Reset failed');
    } finally {
      setIsLoading(false);
    }
  };

  const resetToLogin = () => {
    setIsOtpSelectionView(true);
    setIsEmailOtpView(false);
    setIsSmsOtpView(false);
    setIsForgotPassword(false);
    setOtpSent(false);
    setSmsOtpSent(false);
    setShowNewPasswordForm(false);
    setForgotPasswordMethod('');
    setNewPassword('');
    setConfirmPassword('');
    setEmail('');
    setSmsPhone('');
    setEmailOtp(['', '', '', '', '', '']);
    setSmsOtp(['', '', '', '', '', '']);
  };

  const handleEmailOtpChange = (index, value) => {
    if (value.length <= 1 && /^\d*$/.test(value)) {
      const newOtp = [...emailOtp];
      newOtp[index] = value;
      setEmailOtp(newOtp);
      if (value !== '' && index < 5) {
        document.querySelector(`input[name='login-email-otp-${index + 1}']`)?.focus();
      }
    }
  };

  const handleEmailOtpPaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (!pastedData) return;
    const newOtp = [...emailOtp];
    for (let i = 0; i < 6; i++) {
      newOtp[i] = pastedData[i] || '';
    }
    setEmailOtp(newOtp);
    const nextEmptyIndex = newOtp.findIndex((val) => val === '');
    const focusIndex = nextEmptyIndex === -1 ? 5 : nextEmptyIndex;
    document.querySelector(`input[name='login-email-otp-${focusIndex}']`)?.focus();
  };

  const handleSmsOtpChange = (index, value) => {
    if (value.length <= 1 && /^\d*$/.test(value)) {
      const newOtp = [...smsOtp];
      newOtp[index] = value;
      setSmsOtp(newOtp);
      if (value !== '' && index < 5) {
        document.querySelector(`input[name='login-sms-otp-${index + 1}']`)?.focus();
      }
    }
  };

  const handleSmsOtpPaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (!pastedData) return;
    const newOtp = [...smsOtp];
    for (let i = 0; i < 6; i++) {
      newOtp[i] = pastedData[i] || '';
    }
    setSmsOtp(newOtp);
    const nextEmptyIndex = newOtp.findIndex((val) => val === '');
    const focusIndex = nextEmptyIndex === -1 ? 5 : nextEmptyIndex;
    document.querySelector(`input[name='login-sms-otp-${focusIndex}']`)?.focus();
  };

  return (
    <div className="flex flex-col">
      {/* 0. OTP Input View */}
      {isOtpSelectionView && !isEmailOtpView && !isSmsOtpView && !isForgotPassword && (
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="space-y-3 sm:space-y-3.5 flex-1 flex flex-col"
        >
          <div>
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Login Now</h2>
          </div>

          <form onSubmit={handleSendOtp} className="space-y-3">
            <div className="space-y-1">
              <label className="text-xs sm:text-sm font-semibold text-gray-800 ml-1">
                Email or Mobile Number
              </label>
              <div className="bg-white/60 border border-gray-200 rounded-xl px-3.5 py-2 sm:py-2.5 focus-within:ring-2 focus-within:ring-[#067978] focus-within:border-transparent transition-all flex items-center">
                <input
                  type="text"
                  value={otpIdentifier}
                  onChange={(e) => setOtpIdentifier(e.target.value)}
                  className="w-full bg-transparent text-gray-900 font-medium text-sm sm:text-base outline-none placeholder:text-gray-400"
                  placeholder="user@example.com or 9876543210"
                  autoFocus
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-2.5 sm:py-3 bg-[#067978] hover:bg-[#05605f] text-white font-bold text-base rounded-xl shadow-md hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 cursor-pointer"
            >
              {isLoading ? 'Sending OTP...' : 'Request for OTP'}
            </button>

            <div className="relative flex py-1.5 sm:py-2 items-center">
              <div className="flex-grow border-t border-gray-200" />
              <span className="flex-shrink-0 mx-3 text-gray-400 text-xs font-semibold uppercase tracking-wider">
                Or
              </span>
              <div className="flex-grow border-t border-gray-200" />
            </div>

            <button
              type="button"
              onClick={() => setIsOtpSelectionView(false)}
              className="w-full py-2 sm:py-2.5 border-2 border-[#067978]/20 text-[#067978] font-bold rounded-xl hover:bg-[#067978]/5 transition-all text-sm sm:text-base bg-white/50 cursor-pointer"
            >
              Login with Password
            </button>
          </form>

          <p className="text-center text-xs sm:text-sm text-gray-600 font-medium pt-2 sm:pt-3">
            Don't have an account?
            <button
              type="button"
              onClick={onSwitchToRegister}
              className="text-[#067978] font-bold ml-1 hover:underline cursor-pointer"
            >
              Register Now
            </button>
          </p>
        </motion.div>
      )}

      {/* 1. Password Login View */}
      {!isOtpSelectionView && !isEmailOtpView && !isSmsOtpView && !isForgotPassword && (
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="space-y-3 sm:space-y-3.5 flex-1 flex flex-col"
        >
          <div>
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Login Now</h2>
          </div>
          <form onSubmit={handleLogin} className="space-y-3 sm:space-y-3.5">
            <div className="space-y-1">
              <label className="text-xs sm:text-sm font-semibold text-gray-800 ml-1">
                Email or Phone
              </label>
              <div className="bg-white/60 border border-gray-200 rounded-xl px-3.5 py-2 sm:py-2.5 focus-within:ring-2 focus-within:ring-[#067978] focus-within:border-transparent transition-all flex items-center group">
                <User
                  size={18}
                  className="text-gray-400 group-focus-within:text-[#067978] mr-2.5 transition-colors"
                />
                <input
                  type="text"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="w-full bg-transparent text-gray-900 font-medium text-sm sm:text-base outline-none placeholder:text-gray-400"
                  placeholder="Enter Email or Phone"
                />
              </div>
            </div>
            <div className="space-y-1">
              <div className="flex justify-between items-center px-1">
                <label className="text-xs sm:text-sm font-semibold text-gray-800">Password</label>
                <button
                  type="button"
                  onClick={() => setIsForgotPassword(true)}
                  className="text-[#067978] text-xs font-bold hover:underline cursor-pointer"
                >
                  Forgot?
                </button>
              </div>
              <div className="bg-white/60 border border-gray-200 rounded-xl px-3.5 py-2 sm:py-2.5 focus-within:ring-2 focus-within:ring-[#067978] focus-within:border-transparent transition-all flex items-center group relative">
                <Lock
                  size={18}
                  className="text-gray-400 group-focus-within:text-[#067978] mr-2.5 transition-colors"
                />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full bg-transparent text-gray-900 font-medium text-sm sm:text-base outline-none placeholder:text-gray-400 pr-8"
                  placeholder="Enter password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-[#067978] transition-colors cursor-pointer"
                >
                  {showPassword ? <Eye size={18} /> : <EyeOff size={18} />}
                </button>
              </div>
            </div>
            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-2.5 sm:py-3 bg-[#067978] hover:bg-[#05605f] text-white font-bold text-base sm:text-lg rounded-xl shadow-[0_4px_16px_rgba(6,121,120,0.25)] hover:shadow-[0_6px_20px_rgba(6,121,120,0.35)] hover:-translate-y-0.5 transition-all duration-300 flex items-center justify-center gap-2 mt-1 cursor-pointer"
            >
              {isLoading ? 'Authenticating...' : 'Login'}
            </button>
            <div className="relative flex py-1.5 sm:py-2 items-center">
              <div className="flex-grow border-t border-gray-200" />
              <span className="flex-shrink-0 mx-3 text-gray-400 text-xs font-semibold uppercase tracking-wider">
                Or
              </span>
              <div className="flex-grow border-t border-gray-200" />
            </div>
            <button
              type="button"
              onClick={() => setIsOtpSelectionView(true)}
              className="w-full py-2 sm:py-2.5 border-2 border-[#067978]/20 text-[#067978] font-bold rounded-xl hover:bg-[#067978]/5 hover:border-[#067978]/40 transition-all text-sm sm:text-base bg-white/50 cursor-pointer"
            >
              Login with OTP
            </button>
          </form>
          <p className="text-center text-xs sm:text-sm text-gray-600 font-medium pt-2 sm:pt-3">
            Don't have an account?
            <button
              type="button"
              onClick={onSwitchToRegister}
              className="text-[#067978] font-bold ml-1 hover:underline cursor-pointer"
            >
              Register Now
            </button>
          </p>
        </motion.div>
      )}

      {/* Email OTP View */}
      {isEmailOtpView && !showNewPasswordForm && (
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="space-y-4 flex-1 flex flex-col justify-center"
        >
          <button
            type="button"
            onClick={resetToLogin}
            className="text-gray-600 hover:text-[#067978] text-xs sm:text-sm flex items-center gap-1 font-bold transition-colors cursor-pointer"
          >
            <ChevronRight className="rotate-180" size={16} /> Change Method
          </button>
          {!otpSent ? (
            <form onSubmit={handleSendOtp} className="text-center py-2">
              <div className="w-12 h-12 sm:w-14 sm:h-14 bg-[#067978]/10 rounded-full flex items-center justify-center mx-auto mb-3 text-[#067978]">
                <Mail size={26} />
              </div>
              <h3 className="text-lg sm:text-xl font-bold text-gray-900">Email Verification</h3>
              <p className="text-gray-600 mt-1 mb-4 text-xs sm:text-sm">
                We'll send a code to <b>{email}</b>
              </p>
              <button
                type="submit"
                disabled={isLoading}
                className="w-full py-2.5 sm:py-3 bg-[#067978] text-white rounded-xl font-semibold hover:shadow-lg transition-all text-sm sm:text-base cursor-pointer"
              >
                {isLoading ? 'Sending...' : 'Send Verification Code'}
              </button>
            </form>
          ) : (
            <div className="space-y-4 text-center py-2">
              <div>
                <h3 className="text-lg sm:text-xl font-bold text-gray-900">Verify Email</h3>
                <p className="text-gray-600 mt-1 text-xs sm:text-sm">
                  Enter the code sent to {email}
                </p>
              </div>
              <div className="flex gap-2 justify-center">
                {emailOtp.map((digit, i) => (
                  <input
                    key={i}
                    type="tel"
                    name={`login-email-otp-${i}`}
                    maxLength="1"
                    value={digit}
                    onChange={(e) => handleEmailOtpChange(i, e.target.value)}
                    onPaste={handleEmailOtpPaste}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleVerifyEmailOtp();
                      } else if (e.key === 'Backspace' && !digit && i > 0) {
                        document
                          .querySelector(`input[name='login-email-otp-${i - 1}']`)
                          ?.focus();
                      }
                    }}
                    className="w-9 h-9 sm:w-11 sm:h-11 bg-white/80 border-2 border-gray-200 rounded-xl text-center text-lg sm:text-xl font-bold text-[#067978] focus:border-[#067978] outline-none transition-all shadow-sm"
                    autoFocus={i === 0}
                  />
                ))}
              </div>
              <button
                type="button"
                onClick={handleVerifyEmailOtp}
                disabled={isLoading}
                className="w-full py-2.5 sm:py-3 bg-[#067978] hover:bg-[#05605f] text-white font-bold text-sm sm:text-base rounded-xl shadow-lg transition-all cursor-pointer"
              >
                Verify & Login
              </button>
              <button
                type="button"
                onClick={handleSendOtp}
                className="text-xs sm:text-sm text-gray-500 hover:text-[#067978] font-semibold cursor-pointer"
              >
                Resend Code
              </button>
            </div>
          )}
        </motion.div>
      )}

      {/* SMS OTP View */}
      {isSmsOtpView && !showNewPasswordForm && (
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="space-y-4 flex-1 flex flex-col justify-center"
        >
          <button
            type="button"
            onClick={resetToLogin}
            className="text-gray-600 hover:text-[#067978] text-xs sm:text-sm flex items-center gap-1 font-bold transition-colors cursor-pointer"
          >
            <ChevronRight className="rotate-180" size={16} /> Change Method
          </button>
          {!smsOtpSent ? (
            <form onSubmit={handleSendOtp} className="text-center py-2">
              <div className="w-12 h-12 sm:w-14 sm:h-14 bg-[#067978]/10 rounded-full flex items-center justify-center mx-auto mb-3 text-[#067978]">
                <MessageSquare size={26} />
              </div>
              <h3 className="text-lg sm:text-xl font-bold text-gray-900">SMS Verification</h3>
              <p className="text-gray-600 mt-1 mb-4 text-xs sm:text-sm">
                We'll send a code to <b>+91 {smsPhone}</b>
              </p>
              <button
                type="submit"
                disabled={isLoading}
                className="w-full py-2.5 sm:py-3 bg-[#067978] text-white rounded-xl font-semibold hover:shadow-lg transition-all text-sm sm:text-base cursor-pointer"
              >
                {isLoading ? 'Sending...' : 'Send Verification Code'}
              </button>
            </form>
          ) : (
            <div className="space-y-4 text-center py-2">
              <div>
                <h3 className="text-lg sm:text-xl font-bold text-gray-900">Verify Mobile</h3>
                <p className="text-gray-600 mt-1 text-xs sm:text-sm">
                  Enter the code sent to your phone
                </p>
              </div>
              <div className="flex gap-2 justify-center">
                {smsOtp.map((digit, i) => (
                  <input
                    key={i}
                    type="tel"
                    name={`login-sms-otp-${i}`}
                    maxLength="1"
                    value={digit}
                    onChange={(e) => handleSmsOtpChange(i, e.target.value)}
                    onPaste={handleSmsOtpPaste}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleVerifySmsOtp();
                      } else if (e.key === 'Backspace' && !digit && i > 0) {
                        document
                          .querySelector(`input[name='login-sms-otp-${i - 1}']`)
                          ?.focus();
                      }
                    }}
                    className="w-9 h-9 sm:w-11 sm:h-11 bg-white/80 border-2 border-gray-200 rounded-xl text-center text-lg sm:text-xl font-bold text-[#067978] focus:border-[#067978] outline-none transition-all shadow-sm"
                    autoFocus={i === 0}
                  />
                ))}
              </div>
              <button
                type="button"
                onClick={handleVerifySmsOtp}
                disabled={isLoading}
                className="w-full py-2.5 sm:py-3 bg-[#067978] hover:bg-[#05605f] text-white font-bold text-sm sm:text-base rounded-xl shadow-lg transition-all cursor-pointer"
              >
                Verify & Login
              </button>
              <button
                type="button"
                onClick={handleSendOtp}
                className="text-xs sm:text-sm text-gray-500 hover:text-[#067978] font-semibold cursor-pointer"
              >
                Resend Code
              </button>
            </div>
          )}
        </motion.div>
      )}

      {/* Forgot Password View */}
      {isForgotPassword && !forgotPasswordMethod && (
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="space-y-4 flex-1 flex flex-col justify-center"
        >
          <button
            type="button"
            onClick={resetToLogin}
            className="text-gray-600 hover:text-[#067978] text-xs sm:text-sm flex items-center gap-1 font-bold transition-colors cursor-pointer"
          >
            <ChevronRight className="rotate-180" size={16} /> Back
          </button>
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Reset Password</h2>
          <div className="space-y-2.5">
            <button
              type="button"
              onClick={() => {
                setForgotPasswordMethod('email');
                setIsEmailOtpView(true);
                setOtpSent(false);
              }}
              className="w-full p-3 border border-gray-200 rounded-xl flex items-center gap-3 hover:bg-white/50 transition-colors text-left bg-white/30 cursor-pointer"
            >
              <div className="bg-blue-50 p-2 rounded-lg text-blue-600">
                <Mail size={18} />
              </div>
              <div>
                <div className="font-semibold text-gray-900 text-sm">Email Recovery</div>
                <div className="text-xs text-gray-600">Send code to registered email</div>
              </div>
            </button>
            <button
              type="button"
              onClick={() => {
                setForgotPasswordMethod('sms');
                setIsSmsOtpView(true);
                setSmsOtpSent(false);
              }}
              className="w-full p-3 border border-gray-200 rounded-xl flex items-center gap-3 hover:bg-white/50 transition-colors text-left bg-white/30 cursor-pointer"
            >
              <div className="bg-green-50 p-2 rounded-lg text-green-600">
                <MessageSquare size={18} />
              </div>
              <div>
                <div className="font-semibold text-gray-900 text-sm">SMS Recovery</div>
                <div className="text-xs text-gray-600">Send code to registered phone</div>
              </div>
            </button>
          </div>
        </motion.div>
      )}

      {/* Set New Password View */}
      {showNewPasswordForm && (
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="space-y-4 flex-1 flex flex-col justify-center"
        >
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Set New Password</h2>
          <form onSubmit={resetPassword} className="space-y-3">
            <CustomInput
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="New Password"
              autoFocus
            />
            <CustomInput
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm Password"
            />
            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-2.5 sm:py-3 bg-[#067978] text-white font-bold rounded-xl shadow-lg text-sm sm:text-base cursor-pointer"
            >
              Update Password
            </button>
          </form>
        </motion.div>
      )}

      {/* Role Locked Informative Modal */}
      <RoleLockedModal
        roleLockedInfo={roleLockedInfo}
        onClose={() => setRoleLockedInfo(null)}
      />
    </div>
  );
};

export default LoginForm;
